<template>
  <div ref="wrapEl" class="relative select-none pointer-events-none will-change-transform">
    <!-- Soft glow behind the visual -->
    <div class="absolute inset-0 rounded-full glow-orb-10 scale-75"></div>

    <svg viewBox="0 0 600 600" class="relative w-full h-auto max-w-[560px] mx-auto" fill="none">
      <defs>
        <linearGradient id="hv-stroke" x1="0" y1="0" x2="600" y2="600" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="#3B82F6" />
          <stop offset="100%" stop-color="#60A5FA" />
        </linearGradient>
        <radialGradient id="hv-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#3B82F6" stop-opacity="0.35" />
          <stop offset="100%" stop-color="#3B82F6" stop-opacity="0" />
        </radialGradient>
      </defs>

      <!-- Core glow -->
      <circle cx="300" cy="300" r="170" fill="url(#hv-core)" />

      <!-- Outer dashed orbit — driven by scroll -->
      <g ref="outerEl" class="hv-orbit">
        <circle cx="300" cy="300" r="262" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" stroke-dasharray="3 14" />
        <circle cx="300" cy="38" r="5" fill="#60A5FA" />
        <circle cx="300" cy="38" r="11" stroke="#60A5FA" stroke-opacity="0.35" stroke-width="1.5" class="hv-pulse" />
        <circle cx="562" cy="300" r="4" fill="rgba(255,255,255,0.5)" />
      </g>

      <!-- Middle orbit — counter-rotates on scroll, slow idle spin -->
      <g ref="midEl" class="hv-orbit">
        <g class="hv-spin-slow">
          <circle cx="300" cy="300" r="205" stroke="rgba(96,165,250,0.25)" stroke-width="1.5" />
          <!-- highlighted arc segment -->
          <path d="M 300 95 A 205 205 0 0 1 477 197" stroke="url(#hv-stroke)" stroke-width="3" stroke-linecap="round" />
          <circle cx="477" cy="197" r="6" fill="#3B82F6" />
          <circle cx="123" cy="403" r="5" fill="#60A5FA" />
          <circle cx="123" cy="403" r="12" stroke="#3B82F6" stroke-opacity="0.3" stroke-width="1.5" class="hv-pulse" />
        </g>
      </g>

      <!-- Inner orbit -->
      <g ref="innerEl" class="hv-orbit">
        <circle cx="300" cy="300" r="148" stroke="rgba(255,255,255,0.1)" stroke-width="1" stroke-dasharray="40 22" />
        <circle cx="448" cy="300" r="4" fill="#93C5FD" />
      </g>

      <!-- Connection lines from core to orbits -->
      <g stroke="rgba(96,165,250,0.18)" stroke-width="1">
        <line x1="300" y1="300" x2="477" y2="197" />
        <line x1="300" y1="300" x2="123" y2="403" />
        <line x1="300" y1="300" x2="300" y2="95" />
      </g>

      <!-- Central gear — spins with scroll + gentle idle rotation -->
      <g ref="gearEl" class="hv-orbit">
        <g class="hv-spin">
          <g fill="#1B2A4A" stroke="url(#hv-stroke)" stroke-width="2.5">
            <rect v-for="angle in teeth" :key="angle" x="291" y="212" width="18" height="24" rx="4"
                  :transform="`rotate(${angle} 300 300)`" />
            <circle cx="300" cy="300" r="68" />
          </g>
          <circle cx="300" cy="300" r="42" stroke="rgba(96,165,250,0.4)" stroke-width="1.5" stroke-dasharray="6 8" />
        </g>
      </g>

      <!-- Connected small gears (interactive) -->
      <g v-for="g in activeGears" :key="g.id">
        <line x1="300" y1="300" :x2="g.x" :y2="g.y" stroke="rgba(96,165,250,0.25)" stroke-width="1.5" />
        <g :transform="`translate(${g.x} ${g.y}) scale(${SCALE})`">
          <g class="hv-scroll">
            <g class="hv-spin-rev">
              <g fill="#1B2A4A" stroke="url(#hv-stroke)" stroke-width="3">
                <rect v-for="angle in teeth" :key="angle" x="-9" y="-88" width="18" height="24" rx="4"
                      :transform="`rotate(${angle})`" />
                <circle r="68" />
              </g>
              <circle r="40" stroke="rgba(96,165,250,0.4)" stroke-width="2" stroke-dasharray="6 8" />
            </g>
          </g>
          <circle r="11" fill="#3B82F6" />
        </g>
        <!-- delete button -->
        <g :transform="`translate(${g.bx} ${g.by})`">
          <g class="hv-ctrl pointer-events-auto cursor-pointer" @click="removeGear(g.id)">
            <circle r="13" fill="#1B2A4A" stroke="#60A5FA" stroke-width="1.5" />
            <path d="M -4.5 -4.5 L 4.5 4.5 M 4.5 -4.5 L -4.5 4.5" stroke="#fff" stroke-width="2" stroke-linecap="round" />
          </g>
        </g>
      </g>

      <!-- Add gear button -->
      <g transform="translate(468 452)">
        <g v-if="activeGears.length < MAX" class="hv-ctrl pointer-events-auto cursor-pointer" @click="addGear">
          <circle r="18" fill="#3B82F6" />
          <circle r="18" fill="none" stroke="#60A5FA" stroke-opacity="0.6" stroke-width="2" class="hv-pulse" />
          <path d="M -8 0 H 8 M 0 -8 V 8" stroke="#fff" stroke-width="2.5" stroke-linecap="round" />
        </g>
      </g>

      <!-- Static core dot (does not rotate) -->
      <circle cx="300" cy="300" r="14" fill="#3B82F6" />
      <circle cx="300" cy="300" r="22" stroke="#60A5FA" stroke-opacity="0.5" stroke-width="1.5" class="hv-pulse" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

// Interactive meshed gears: up to 4 small gears in fixed diagonal slots.
const MAX = 4;
const SCALE = 0.55;
const D = 132;
const SLOTS = [-45, 45, 135, 225].map((deg, id) => {
  const r = (deg * Math.PI) / 180;
  return {
    id,
    x: +(300 + D * Math.cos(r)).toFixed(1),
    y: +(300 + D * Math.sin(r)).toFixed(1),
    bx: +(300 + (D + 52) * Math.cos(r)).toFixed(1),
    by: +(300 + (D + 52) * Math.sin(r)).toFixed(1),
  };
});
const activeIds = ref<number[]>([]);
const activeGears = computed(() => SLOTS.filter((s) => activeIds.value.includes(s.id)));
const addGear = () => {
  if (activeIds.value.length >= MAX) return;
  const free = SLOTS.find((s) => !activeIds.value.includes(s.id));
  if (free) activeIds.value.push(free.id);
};
const removeGear = (id: number) => {
  activeIds.value = activeIds.value.filter((x) => x !== id);
};

const wrapEl = ref<HTMLElement | null>(null);
const outerEl = ref<SVGGElement | null>(null);
const midEl = ref<SVGGElement | null>(null);
const innerEl = ref<SVGGElement | null>(null);
const gearEl = ref<SVGGElement | null>(null);

const teeth = [0, 45, 90, 135, 180, 225, 270, 315];

let target = 0;
let smooth = 0;
let rafId = 0;
let running = false;

// Scroll-rotation layers of the small gears (collected from the DOM since
// they are added/removed dynamically).
let smallEls: SVGGElement[] = [];
const refreshSmall = () => {
  smallEls = wrapEl.value
    ? Array.from(wrapEl.value.querySelectorAll<SVGGElement>(".hv-scroll"))
    : [];
};

// Transforms are written straight to the DOM here instead of via template
// bindings — going through Vue's renderer every frame is too slow for scroll.
const apply = () => {
  const deg = smooth * 0.12;
  if (wrapEl.value) wrapEl.value.style.transform = `translateY(${smooth * 0.06}px)`;
  if (outerEl.value) outerEl.value.style.transform = `rotate(${deg * 0.35}deg)`;
  if (midEl.value) midEl.value.style.transform = `rotate(${deg * -0.55}deg)`;
  if (innerEl.value) innerEl.value.style.transform = `rotate(${deg * 0.8}deg)`;
  if (gearEl.value) gearEl.value.style.transform = `rotate(${deg * 1.4}deg)`;
  // Small gears mesh with the central gear: counter-rotate, faster (smaller radius).
  const smallDeg = -deg * 1.4 * 1.82;
  for (const el of smallEls) el.style.transform = `rotate(${smallDeg}deg)`;
};

// Re-collect the small-gear layers whenever gears are added/removed,
// then immediately sync them to the current scroll rotation.
watch(
  () => activeIds.value.length,
  () => nextTick(() => { refreshSmall(); apply(); })
);

const tick = () => {
  smooth += (target - smooth) * 0.08;
  if (Math.abs(target - smooth) < 0.05) {
    smooth = target;
    apply();
    running = false;
    return;
  }
  apply();
  rafId = requestAnimationFrame(tick);
};

let visible = true;
let observer: IntersectionObserver | null = null;

const onScroll = () => {
  target = window.scrollY;
  if (!visible) return;
  if (!running) {
    running = true;
    rafId = requestAnimationFrame(tick);
  }
};

onMounted(() => {
  target = smooth = window.scrollY;
  apply();
  window.addEventListener("scroll", onScroll, { passive: true });

  // Off-screen: pause idle animations and skip scroll-driven updates entirely.
  if (wrapEl.value && "IntersectionObserver" in window) {
    observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        wrapEl.value?.classList.toggle("hv-paused", !visible);
        if (visible) {
          smooth = target = window.scrollY;
          apply();
        }
      },
      { rootMargin: "100px" }
    );
    observer.observe(wrapEl.value);
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
  cancelAnimationFrame(rafId);
  observer?.disconnect();
});
</script>

<style scoped>
.hv-orbit,
.hv-spin,
.hv-spin-slow {
  transform-origin: 300px 300px;
  transform-box: view-box;
}

.hv-spin {
  animation: hv-rotate 40s linear infinite;
}

.hv-spin-slow {
  animation: hv-rotate 90s linear infinite reverse;
}

/* Scroll-driven rotation layer for small gears (JS sets the transform). */
.hv-scroll {
  transform-box: fill-box;
  transform-origin: center;
}

/* Small meshed gears counter-rotate around their own centre. */
.hv-spin-rev {
  transform-box: fill-box;
  transform-origin: center;
  animation: hv-rotate 26s linear infinite reverse;
}

/* Interactive controls (add / delete buttons). */
.hv-ctrl {
  transform-box: fill-box;
  transform-origin: center;
  transition: transform 0.18s ease;
}
.hv-ctrl:hover {
  transform: scale(1.15);
}

.hv-pulse {
  transform-box: fill-box;
  transform-origin: center;
  animation: hv-pulse 2.5s ease-in-out infinite;
}

@keyframes hv-rotate {
  to {
    transform: rotate(360deg);
  }
}

@keyframes hv-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.1;
  }
}

.hv-paused .hv-spin,
.hv-paused .hv-spin-slow,
.hv-paused .hv-spin-rev,
.hv-paused .hv-pulse {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  .hv-spin,
  .hv-spin-slow,
  .hv-spin-rev,
  .hv-pulse {
    animation: none;
  }
}
</style>
